import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'hooks/config/queryClient';
import {
  createFile,
  createTemplate,
  fileSharingV2,
  recallSignatureRequest,
  saveSummary,
  saveTags,
  sendESigantureRequest,
  shareFile,
  updateFile,
} from 'services/fileManager/fileManagerServices';

export function useFileMutations() {
  const queryClient = useQueryClient();
  const invalidateFileManager = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.tags() });
    queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() });
  };

  const createFileMutation = useMutation({
    mutationFn: (payload) => createFile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() });
      queryClient.invalidateQueries({ queryKey: ['file-manager', 'folders'] });
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: (payload) => updateFile(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() }),
  });

  const shareFileMutation = useMutation({
    mutationFn: (payload) => shareFile(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() }),
  });

  const fileSharingV2Mutation = useMutation({
    mutationFn: (payload) => fileSharingV2(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() }),
  });

  const saveTagsMutation = useMutation({
    mutationFn: (payload) => saveTags(payload),
    onSuccess: (_, variables) => {
      const caseId = variables?.caseIdForInvalidation;
      if (caseId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.tags(caseId || '') });
      }
    },
  });

  const sendESignatureRequestMutation = useMutation({
    mutationFn: (payload) => sendESigantureRequest(payload),
    onSuccess: () => invalidateFileManager(),
  });

  const recallSignatureRequestMutation = useMutation({
    mutationFn: (payload) => recallSignatureRequest(payload),
    onSuccess: () => invalidateFileManager(),
  });

  const saveSummaryMutation = useMutation({
    mutationFn: (payload) => saveSummary(payload),
    onSuccess: () => invalidateFileManager(),
  });

  const createTemplateMutation = useMutation({
    mutationFn: (payload) => createTemplate(payload),
    onSuccess: () => invalidateFileManager(),
  });

  return {
    createFile: (payload) => createFileMutation.mutateAsync(payload),
    updateFile: (payload) => updateFileMutation.mutateAsync(payload),
    shareFile: (payload) => shareFileMutation.mutateAsync(payload),
    fileSharingV2: (payload) => fileSharingV2Mutation.mutateAsync(payload),
    saveTags: (payload) => saveTagsMutation.mutateAsync(payload),
    sendESignatureRequest: (payload) => sendESignatureRequestMutation.mutateAsync(payload),
    recallSignatureRequest: (payload) => recallSignatureRequestMutation.mutateAsync(payload),
    saveSummary: (payload) => saveSummaryMutation.mutateAsync(payload),
    createTemplate: (payload) => createTemplateMutation.mutateAsync(payload),
  };
}
